import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import apiService from '@/services/api.service';
import { logger } from '@/utils/logger';
import { API_ENDPOINTS } from '@/constants/api.constants';
import type { User as BaseUser, UserRole } from '@/types/models';
import type { ApiResponse } from '@/types/api';
import { AppError } from '@/utils/errors';
import UserService from '@/services/user.service';

interface AuthUser extends Omit<BaseUser, 'role' | 'createdAt' | 'updatedAt'> {
    role: UserRole | string;
    clubId?: number;
    exp?: number;
    iat?: number;
    createdAt?: string;
    updatedAt?: string;
}

// Определяем все возможные форматы ответа
interface LoginResponse {
    user: AuthUser;
}

interface AuthContextType {
    isAuthenticated: boolean;
    user: AuthUser | null;
    isLoading: boolean;
    login: (email: string, password: string, remember?: boolean) => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState<AuthUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Функция для проверки авторизации через запрос к серверу
    const checkAuth = async () => {
        setIsLoading(true);
        try {
            // Пытаемся получить текущего пользователя - токены в httpOnly cookies отправляются автоматически
            const currentUser = await UserService.getCurrentUser();
            const authUser: AuthUser = {
                ...currentUser,
                clubId: currentUser.clubId // Явно сохраняем clubId
            };
            setUser(authUser);
            setIsAuthenticated(true);            
            return true;
        } catch (error: any) {
            // Пользователь не авторизован
            // Игнорируем 401, 429 и 500 ошибки (429 - rate limit, 500 - может быть при отсутствии refresh token)
            const status = error?.response?.status;
            if (status === 401) {
                // Пользователь не авторизован - это нормально
                
            } else if (status === 429) {
                // Rate limit - не критично, не сбрасываем авторизацию
                logger.warn('Rate limit exceeded during auth check, keeping current auth state');
                // Не сбрасываем состояние авторизации при rate limit
                return isAuthenticated; // Возвращаем текущее состояние
            } else if (status === 500) {
                // 500 ошибка может быть при попытке refresh token для неавторизованного пользователя
                // Это нормальная ситуация для неавторизованных пользователей
                logger.debug('Auth check failed with 500 (likely no refresh token for unauthenticated user)');
            } else {
                // Другие ошибки логируем как debug, так как для неавторизованных пользователей это нормально
                logger.debug('Error checking auth (expected for unauthenticated users):', {
                    status,
                    message: error?.message
                });
            }
            
            // Сбрасываем авторизацию только если это не rate limit
            if (status !== 429) {
                setUser(null);
                setIsAuthenticated(false);
            }
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // Получаем CSRF токен при первой загрузке приложения
        const fetchCsrfToken = async () => {
            try {
                await apiService.get('/csrf-token');
            } catch (error) {
                // Игнорируем ошибки получения CSRF токена
                logger.debug('Failed to fetch initial CSRF token:', error);
            }
        };
        
        fetchCsrfToken();
        
        // Проверяем авторизацию при загрузке компонента
        // НЕ проверяем на странице входа, чтобы избежать лишних запросов и ошибок 401 в консоли
        const isLoginPage = window.location.pathname === '/login' || 
                           window.location.pathname === '/register' ||
                           window.location.pathname === '/forgot-password';
        
        if (!isLoginPage) {
            checkAuth();
        } else {
            // На странице входа просто устанавливаем isLoading в false
            setIsLoading(false);
        }
    }, []);

    const login = async (email: string, password: string, remember: boolean = false) => {
        try {
            logger.debug('Attempting login:', { email });
            const response = await apiService.post<ApiResponse<LoginResponse>>(API_ENDPOINTS.AUTH.LOGIN, {
                email,
                password,
            });

            logger.debug('Login response:', response.data);

            const responseData = response.data?.data;

            if (!responseData) {
                throw new AppError('Empty response data');
            }

            const userData = responseData.user;

            if (!userData) {
                throw new AppError('Invalid server response');
            }

            // Токены теперь в httpOnly cookies, они устанавливаются сервером автоматически
            // Сохраняем только данные пользователя
            const authUser: AuthUser = {
                ...userData,
                clubId: userData.clubId // Явно сохраняем clubId
            };

            if (remember) {
                localStorage.setItem('savedEmail', email);
                localStorage.setItem('savedPassword', btoa(password));
            } else {
                localStorage.removeItem('savedEmail');
                localStorage.removeItem('savedPassword');
            }            

            setUser(authUser);
            setIsAuthenticated(true);            
        } catch (error) {
            logger.error('Login error:', {
                error: error instanceof Error ? error.message : 'Unknown error',
                email
            });

            // Определяем тип ошибки и возвращаем понятное сообщение
            if (axios.isAxiosError(error)) {
                const status = error.response?.status;
                switch (status) {
                    case 401:
                        throw new Error('Неверный email или пароль');
                    case 400:
                        throw new Error('Пожалуйста, проверьте правильность введенных данных');
                    case 429:
                        throw new Error('Слишком много попыток входа. Пожалуйста, подождите');
                    default:
                        throw new Error('Произошла ошибка при входе в систему');
                }
            }

            throw new Error('Не удалось выполнить вход в систему');
        }
    };

    const logout = async () => {
        try {
            logger.debug('Logging out user');
            
            // Вызываем endpoint logout на сервере для очистки cookies
            try {
                await apiService.post(API_ENDPOINTS.AUTH.LOGOUT);
            } catch (error) {
                // Игнорируем ошибки при logout, так как cookies могут быть уже очищены
                logger.debug('Logout endpoint error (ignored):', error);
            }

            // Очищаем состояние
            setUser(null);
            setIsAuthenticated(false);

            // Добавляем задержку перед перенаправлением
            setTimeout(() => {
                // Проверяем, что мы не на странице входа
                if (window.location.pathname !== '/login') {
                    window.location.href = '/login';
                }
            }, 100);
        } catch (error) {
            logger.error('Logout error:', error);
            // Даже при ошибке очищаем состояние
            setUser(null);
            setIsAuthenticated(false);
            window.location.href = '/login';
        }
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, user, isLoading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};